import cleaner from 'deep-cleaner';
import mapJson from 'jsonpath-mapper';
import { Node as DeliveryNode } from 'contensis-delivery-api';
import { Node } from 'contensis-management-api';

import ContensisRepository from '~/repositories/ContensisRepository';
import mapErrorToObject from './mapAppErrorToObject';
import { mapSourceNodeToTargetNode } from './mapSourceNodeToTargetNode';
import { generateGuid } from './transformGuids';
import { compareFields } from '~/util/EntryHelper';
import { Logger } from '~/util/logger';
import { merge } from '~/util/merge';
import { getParentPath } from '~/util/path';

import { MigrateStatus } from '~/models/Migration';
import { BuildNodeOptions, MigrateNodeModel } from '~/models/Nodes';
import { firstValueOf } from '~/util/object';

/**
 * Build a complete node specifically for the target repository, providing
 * context of source and target repositories we can transform a given source node
 * into a migration-ready node structure checking dependencies and setting
 * migration status appropriately
 *
 * Output the node as JSON for loading into the Management API using the `toJSON` method
 */
export class NodeBuilder implements MigrateNodeModel {
  existing?: DeliveryNode;
  node!: Node | DeliveryNode;
  status: MigrateStatus = 'create';
  error?: MappedError;
  diff?: string;

  isRootNode!: boolean;
  isRootChild!: boolean;
  rootNodeId!: string;
  existingAtPath?: DeliveryNode;
  #onError: BuildNodeOptions['onError'];

  get contentTypeId() {
    return this.sourceNode.entry?.sys.contentTypeId;
  }

  get displayName() {
    return firstValueOf(this.node.displayName) || '';
  }

  get entryId() {
    return (this.node as any).entryId;
  }

  get id() {
    return this.node.id;
  }

  get originalId() {
    return this.sourceNode.id;
  }

  get parentId() {
    return this.existing?.parentId || this.node.parentId;
  }

  get path() {
    return this.sourceNode.path;
  }

  constructor(
    public contensis: ContensisRepository,
    public source: ContensisRepository,
    public sourceNode: DeliveryNode,
    { onError }: BuildNodeOptions,
    public isAncestorNode = false
  ) {
    this.isAncestorNode = isAncestorNode;
    this.#onError = onError;

    this.#init();
    this.#build();
    this.status = this.#checkStatus();
  }

  /**
   * Return the node as JSON for API operations
   */
  toJSON = () => {
    return this.node;
  };

  /**
   * Initialize the node builder with context and existing data
   */
  #init = () => {
    const { contensis, source, sourceNode } = this;

    // Determine node context
    this.isRootNode =
      sourceNode.id === source.nodes.tree?.id ||
      !!source.models.resources?.nodes.find((n) => n.id === sourceNode.id);

    this.isRootChild =
      (source.nodes.tree?.parentId === null &&
        sourceNode.parentId === source.nodes.tree.id) ||
      (source.nodes.rootAncestor?.id || '') === sourceNode.parentId;

    this.rootNodeId =
      contensis.nodes.rootAncestor?.id || contensis.nodes.tree?.id || '';

    // If the project's tree root path is just '/' we don't want to slice it in our path comparisons
    const sourceTreeRootLength =
      source.nodes.treeRootPath.length === 1
        ? 0
        : source.nodes.treeRootPath.length;
    const targetTreeRootLength =
      contensis.nodes.treeRootPath.length === 1
        ? 0
        : contensis.nodes.treeRootPath.length;

    // Find existing node at path
    this.existingAtPath = contensis.nodes.raw.find(
      (n: any) =>
        // slice the path to ignore tree root differences such as /en-gb and /
        n.path.slice(targetTreeRootLength) ===
        sourceNode.path.slice(sourceTreeRootLength)
    );

    // Find existing node by ID will be set after node is built
  };

  /**
   * Build the target node from the source node
   */
  #build = () => {
    const { contensis, sourceNode, isRootChild, rootNodeId, isAncestorNode } =
      this;

    try {
      // Map source node to target node structure
      this.node = mapSourceNodeToTargetNode(
        contensis,
        sourceNode,
        isRootChild ? rootNodeId : undefined
      );

      // Don't map entryId from sourceNode if we are creating this node as an ancestor in the target
      if (isAncestorNode) {
        delete this.node.entryId;
      }

      this.#handleModelDefaults();
      this.#ensureNodeId();
      this.#mergeExistingNode();
      this.#ensureParentId();
    } catch (ex: any) {
      this.error = mapErrorToObject(ex);
      this.error.message = `Error building node ${sourceNode.id}: ${this.error.message}`;
    }
  };

  /**
   * Handle model defaults and ignore logic
   */
  #handleModelDefaults = () => {
    const { contensis, sourceNode } = this;
    const migrateModels = contensis.models.toMigrate;

    // If this node is part of defaults in a model migration, and it has been
    // superceded by an existing default we will ignore it
    const migrateParents =
      contensis.nodes.migrateNodes.filter(
        (mn: any) => getParentPath(mn.path) === sourceNode.path
      ) || [];

    if (
      (migrateModels.find((c) =>
        c.rejectedDefaults.find((rd) => rd[0] === sourceNode.id)
      ) &&
        !migrateModels.find((c) =>
          c.defaults.find((rd) => rd[0] === this.node.id)
        )) ||
      (migrateModels.length &&
        migrateParents.length &&
        migrateParents.every((mn: any) => mn.status === 'ignore'))
    ) {
      this.status = 'ignore';
    }
  };

  /**
   * Ensure the node has a valid ID
   */
  #ensureNodeId = () => {
    const { contensis, sourceNode } = this;

    // Always generate a new node id if we cannot resolve one
    if (!this.node.id) {
      this.node.id = generateGuid(
        contensis.alias,
        contensis.projectId,
        '00000000-0000-0000-0000-00000000000',
        sourceNode.path
      );
    }
  };

  #mergeExistingNode = () => {
    const { contensis, sourceNode } = this;
    // Update existing reference after ID is set
    this.existing = contensis.nodes.raw.find((n: any) => n.id === this.node.id);

    // Check for potential path conflicts where a node exists at the target path
    // but with a different ID to our source node and we are not ignoring errors
    if (
      this.existing &&
      this.existingAtPath &&
      this.existing.id !== this.existingAtPath.id &&
      sourceNode.path !== '/' &&
      !contensis.context.payload.ignoreErrors
    ) {
      this.status = 'error';
      this.error = mapErrorToObject(
        new Error(
          `Another node already exists at path '${sourceNode.path}' (this error could be ignored)`
        )
      );
    }

    // Promote existingAtPath to be existing as we need to know there is an existing node at this path
    if (!this.existing && this.existingAtPath)
      this.existing = this.existingAtPath;

    if (this.existing) {
      // Merge the built node with the existing node to retain attributes
      this.node = cleaner(
        merge(
          this.existing,
          cleaner(
            this.node,
            // Ensure we take these attribute values from the existing node
            ['id', 'parentId', 'includeInMenu', 'isCanonical']
          )
        ),
        // Drop these delivery api only fields from the merged target node
        ['childCount', 'entry', 'language', 'path', 'version']
      );
    }
  };

  /**
   * Ensure the node has a valid parent ID
   */
  #ensureParentId = () => {
    const { sourceNode, isRootNode, isAncestorNode, existing, existingAtPath } =
      this;
    const parentPath = getParentPath(sourceNode.path);

    if (
      (isRootNode || isAncestorNode) &&
      !existing &&
      sourceNode.path !== '/'
    ) {
      this.#resolveParentForRootNode(parentPath);
    }

    if (!this.node.parentId && sourceNode.path !== '/') {
      this.#findParentFromKnownData(parentPath);
    }

    // Set parentId in the target node when our root node is
    // something other than '/' to be an existing parent guid
    if (
      existingAtPath &&
      isRootNode &&
      sourceNode.path !== '/' &&
      !this.node.parentId
    ) {
      this.node.parentId = existingAtPath.parentId;
    }
  };

  /**
   * Resolve parent node for root nodes
   */
  #resolveParentForRootNode = (parentPath: string) => {
    const { contensis, status } = this;

    // Ensure a parent node exists for this root or ancestor node
    const nodeAncestor =
      [
        ...(contensis.nodes.ancestors || []),
        ...(contensis.nodes.raw || []),
      ].find(
        (a: any) => a.id === this.node.parentId || a.path === parentPath
      ) || this.#willParentExist(parentPath)?.node;

    if (nodeAncestor) {
      // Set this root node parentId to be the id of the existing root ancestor
      this.node.parentId = nodeAncestor.id;
    } else if (status === 'ignore') {
      // Don't go shimming ancestors if we're ignoring this node
      return;
    } else {
      this.#createMissingAncestor(parentPath);
    }
  };

  /**
   * Create missing ancestor nodes
   */
  #createMissingAncestor = (parentPath: string) => {
    const { contensis, source, sourceNode } = this;

    // No ancestor for this root node - create a new node for this parent
    const sourceAncestor = source.nodes.ancestors.find(
      (a) => a.path === parentPath
    );

    if (sourceAncestor) {
      if (sourceAncestor.parentId) {
        // Build a missing node ancestor so we can set the correct parentId for this node
        const targetAncestor = new NodeBuilder(
          contensis,
          source,
          sourceAncestor,
          { onError: this.#onError },
          true
        );
        contensis.nodes.migrateNodes.push(targetAncestor);
        this.node.parentId = targetAncestor.id;
      } else {
        this.#handleSiteRootNode();
      }
    } else {
      Logger.warning(
        `Unable to find or create an ancestor for node at path ${sourceNode.path}, the migration may fail`
      );
    }
  };

  /**
   * Handle site root node setup
   */
  #handleSiteRootNode = () => {
    const { contensis } = this;

    // if parentId is null we have hit the root of the site tree
    // and we must take the existing parent id from the target's tree
    const siteRootNode = contensis.nodes.treeRootNode;

    this.node.parentId = siteRootNode?.id;

    if (siteRootNode) {
      const targetRoot = new NodeBuilder(
        contensis,
        contensis,
        siteRootNode as DeliveryNode,
        { onError: this.#onError },
        true
      );
      contensis.nodes.migrateNodes.push(targetRoot);
    }
  };

  /**
   * Find parent ID from known data
   */
  #findParentFromKnownData = (parentPath: string) => {
    const { contensis } = this;

    const parentExists = contensis.nodes.raw.find(
      (n: any) => n.path === parentPath
    );
    if (parentExists) {
      this.node.parentId = parentExists.id;
    } else {
      const parentWillExist = this.#willParentExist(parentPath);
      if (parentWillExist) {
        this.node.parentId = parentWillExist.id;
      }
    }
  };

  #willParentExist(parentPath: string) {
    const parentWillExist = this.contensis.nodes.migrateNodes.find(
      (n) => n.path === parentPath
    );
    return parentWillExist;
  }
  /**
   * Check the node for migration status
   */
  #checkStatus = (): MigrateStatus => {
    const { error, existing, status, sourceNode, contensis, source } = this;

    if (error) return 'error';
    if (status === 'ignore') return 'ignore';

    // Check for missing parent
    if (!this.node.parentId && sourceNode.path !== '/') {
      this.status = 'error';
      this.error = mapErrorToObject(
        new Error(
          `An immediate parentId could not be set for '${sourceNode.path}'`
        )
      );
      return 'error';
    }

    // Compare with existing node
    if (existing || sourceNode.path === '/') {
      const language = contensis.project?.primaryLanguage || 'en-GB';

      const { changed, diff } = compareFields(
        this.node as any,
        mapJson(existing, {
          displayName: {
            [language]: [`displayName.${language}`, 'displayName', 'title'],
          },
          entryId: 'entry.sys.id',
          id: 'id',
          includeInMenu: 'includeInMenu',
          isCanonical: 'isCanonical',
          parentId: 'parentId',
          projectId: 'projectId',
          slug: {
            [language]: [`slug.${language}`, 'slug'],
          },
        }),
        source,
        contensis
      );

      if (changed) {
        this.diff = diff;
        return 'update';
      } else {
        return 'no change';
      }
    }

    return 'create';
  };
}
